import React, { useMemo, useState } from "react";
import {
    closestCenter,
    DndContext,
    DragEndEvent,
    PointerSensor,
    useSensor,
    useSensors,
} from "@dnd-kit/core";
import { restrictToVerticalAxis } from "@dnd-kit/modifiers";
import {
    SortableContext,
    useSortable,
    verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

import { ICommandTemplate } from "@formant/core/model/ICommandTemplate";
import { Uuid } from "@formant/core/model/Uuid";
import {
    Button,
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
    Terminal,
    Trash2,
    useToast,
} from "@formant/ui-kit";

import { commandTemplateStore } from "../../../../stores";
import { formatDate } from "../../../../utils/formatDate";
import { TagPills } from "../utils/TagPills";

interface ICommandTemplatesListProps {
    onCommandTemplateEdit: (commandTemplate: ICommandTemplate) => void;
    commands: ICommandTemplate[];
    canReorder?: boolean;
    onReordered?: () => void;
}

interface ISortableCommandRowProps {
    command: ICommandTemplate;
    canReorder: boolean;
    onEdit: (command: ICommandTemplate) => void;
    onDelete: (command: ICommandTemplate) => void;
}

const SortableCommandRow: React.FC<ISortableCommandRowProps> = ({
    command,
    canReorder,
    onEdit,
    onDelete,
}) => {
    const { attributes, listeners, setNodeRef, transform, transition } =
        useSortable({
            id: command.id || command.name,
            disabled: !canReorder || !command.id,
        });

    return (
        <TableRow
            ref={setNodeRef}
            className=""
            style={{
                transform: CSS.Transform.toString(transform),
                transition,
            }}
            onClick={() => onEdit(command)}
        >
            <TableCell className="w-10">
                <button
                    type="button"
                    className="cursor-grab active:cursor-grabbing p-1"
                    data-testid={`command-reorder-${command.name}`}
                    title={
                        canReorder
                            ? "Drag to set Issue a Command order"
                            : "Clear search and tag filters to reorder"
                    }
                    disabled={!canReorder}
                    onClick={event => {
                        event.preventDefault();
                        event.stopPropagation();
                    }}
                    {...attributes}
                    {...listeners}
                >
                    <span aria-hidden="true">⋮⋮</span>
                </button>
            </TableCell>
            <TableCell className="flex items-center gap-2">
                <Terminal className="h-4 w-4 " />
                {command.name}
            </TableCell>
            <TableCell>
                <TagPills tags={command.tags} />
            </TableCell>
            <TableCell>
                {formatDate(new Date(command.createdAt || ""))}
            </TableCell>
            <TableCell>
                {formatDate(new Date(command.updatedAt || ""))}
            </TableCell>
            <TableCell>
                <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8"
                    onClick={e => {
                        e.preventDefault();
                        e.stopPropagation();
                        onDelete(command);
                    }}
                >
                    <Trash2 className="h-4 w-4 " />
                </Button>
            </TableCell>
        </TableRow>
    );
};

export const CommandTemplatesList: React.FC<ICommandTemplatesListProps> = ({
    onCommandTemplateEdit,
    commands,
    canReorder = true,
    onReordered,
}) => {
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [commandToDelete, setCommandToDelete] =
        useState<ICommandTemplate | null>(null);
    const { toast } = useToast();
    const sensors = useSensors(
        useSensor(PointerSensor, {
            activationConstraint: {
                distance: 4,
            },
        }),
    );
    const sortableIds = useMemo(
        () => commands.map(command => command.id || command.name),
        [commands],
    );

    const handleDeleteClick = (command: ICommandTemplate) => {
        setCommandToDelete(command);
        setDeleteDialogOpen(true);
    };

    const confirmDelete = () => {
        if (commandToDelete) {
            setDeleteDialogOpen(false);
            setCommandToDelete(null);
            if (!commandToDelete.id) {
                return;
            }
            commandTemplateStore
                .disable(commandToDelete.id)
                .then(() => {
                    toast({
                        title: "Command deleted",
                        description: "Command deleted successfully",
                    });
                    onReordered?.();
                })
                .catch((_e: any) => {
                    toast({
                        title: "Error deleting command",
                        description: "Error deleting command",
                        variant: "destructive",
                    });
                });
        }
    };

    const handleDragEnd = async (event: DragEndEvent) => {
        const { active, over, delta } = event;
        if (!canReorder || !over || active.id === over.id) {
            return;
        }
        try {
            await commandTemplateStore.reorder(
                active.id as Uuid,
                delta.y < 0 ? "before" : "after",
                over.id as Uuid,
            );
            onReordered?.();
        } catch (_e) {
            toast({
                title: "Error reordering commands",
                description: "Could not save command order",
                variant: "destructive",
            });
        }
    };

    return (
        <div className="flex-1 flex flex-col">
            <div className="flex-1">
                <div className="flex flex-col h-full overflow-hidden">
                    <div className="flex-1 overflow-auto">
                        <DndContext
                            sensors={sensors}
                            collisionDetection={closestCenter}
                            modifiers={[restrictToVerticalAxis]}
                            onDragEnd={handleDragEnd}
                        >
                            <SortableContext
                                items={sortableIds}
                                strategy={verticalListSortingStrategy}
                            >
                                <Table>
                                    <TableHeader className="sticky top-0 bg-formant-color-background-primary z-10">
                                        <TableRow className="">
                                            <TableHead className="w-10" />
                                            <TableHead className="">
                                                NAME
                                            </TableHead>
                                            <TableHead className="">
                                                TAGS
                                            </TableHead>
                                            <TableHead className="">
                                                CREATED
                                            </TableHead>
                                            <TableHead className="">
                                                UPDATED
                                            </TableHead>
                                            <TableHead className="w-10" />
                                        </TableRow>
                                    </TableHeader>
                                    <TableBody>
                                        {commands.map(command => (
                                            <SortableCommandRow
                                                key={command.id || command.name}
                                                command={command}
                                                canReorder={canReorder}
                                                onEdit={onCommandTemplateEdit}
                                                onDelete={handleDeleteClick}
                                            />
                                        ))}
                                    </TableBody>
                                </Table>
                            </SortableContext>
                        </DndContext>
                    </div>
                </div>
            </div>

            <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
                <DialogContent className="">
                    <DialogHeader>
                        <DialogTitle>Confirm Deletion</DialogTitle>
                        <DialogDescription className="">
                            Are you sure you want to delete the command "
                            {commandToDelete?.name}"? This action cannot be
                            undone.
                        </DialogDescription>
                    </DialogHeader>
                    <DialogFooter className="gap-2 sm:gap-0">
                        <Button
                            variant="outline"
                            className=""
                            onClick={() => setDeleteDialogOpen(false)}
                        >
                            Cancel
                        </Button>
                        <Button variant="destructive" onClick={confirmDelete}>
                            Delete
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
};
